// ProductPicker — searchable product autocomplete for quotation/invoice line items.
// Searches across name, model, SKU, brand, category, HSN. Falls back to "Manual" entry.
// Dropdown positions via portal-style fixed positioning so it never gets clipped by the
// quotation modal, table boundary, or sticky bottom buttons.

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import { MagnifyingGlass, X, Tag, Package, Plus } from "@phosphor-icons/react";

const matchScore = (p, q) => {
  if (!q) return 0;
  const qq = q.trim().toLowerCase();
  if (!qq) return 0;
  let s = 0;
  const fields = [
    [p.name, 10], [p.model, 9], [p.sku, 8], [p.brand_name || p.brand, 7],
    [p.category, 5], [p.subcategory, 4], [p.hsn || p.hsn_sac, 6],
    [p.description, 3], [p.tags?.join(" "), 2],
  ];
  for (const [f, weight] of fields) {
    const v = String(f || "").toLowerCase();
    if (!v) continue;
    if (v === qq) s += weight * 3;
    else if (v.startsWith(qq)) s += weight * 2;
    else if (v.includes(qq)) s += weight;
  }
  return s;
};

const ProductPicker = ({
  testId,
  value,              // selected product_id ("" | null = Manual)
  products = [],
  onSelect,           // (product | null) => void  — null means "Manual"
  placeholder = "Search product or select Manual",
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [popupStyle, setPopupStyle] = useState({});
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  // Display label for current selection
  const selected = useMemo(
    () => (value ? products.find((p) => p.product_id === value) : null),
    [value, products]
  );

  const computePosition = useCallback(() => {
    const el = boxRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Position the popup below the box by default, flip above if not enough room
    const spaceBelow = window.innerHeight - r.bottom;
    const maxHeight = 320;
    const flipUp = spaceBelow < 260 && r.top > spaceBelow;
    const top = flipUp ? Math.max(8, r.top - maxHeight - 4) : r.bottom + 4;
    setPopupStyle({
      position: "fixed",
      top: `${top}px`,
      left: `${r.left}px`,
      width: `${Math.max(320, r.width)}px`,
      maxHeight: `${maxHeight}px`,
      zIndex: 9999,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    computePosition();
    const handler = () => computePosition();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    const close = (e) => {
      if (!boxRef.current) return;
      const popup = document.querySelector(`[data-picker-popup="${testId}"]`);
      if (boxRef.current.contains(e.target)) return;
      if (popup && popup.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
      document.removeEventListener("mousedown", close);
    };
  }, [open, computePosition, testId]);

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return products.slice(0, 50);
    return products
      .map((p) => ({ p, s: matchScore(p, q) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 50)
      .map((x) => x.p);
  }, [products, query]);

  const handleSelect = (p) => {
    onSelect?.(p);
    setOpen(false);
    setQuery("");
  };
  const handleManual = () => {
    onSelect?.(null);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={boxRef} className="relative" data-testid={testId}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => { setOpen((v) => !v); setTimeout(() => inputRef.current?.focus(), 0); }}
        className={`w-full rounded border border-slate-200 p-2 text-xs bg-white text-left outline-none flex items-center justify-between gap-1 hover:border-slate-400 ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        {selected ? (
          <span className="truncate font-medium text-slate-800" title={selected.name}>
            <Package size={10} weight="duotone" className="inline -mt-0.5 mr-1 text-[#002FA7]" />
            {selected.name}
          </span>
        ) : value === null || value === "" ? (
          <span className="text-slate-400 truncate">Manual / search…</span>
        ) : (
          <span className="text-slate-400 truncate">{placeholder}</span>
        )}
        <MagnifyingGlass size={12} className="text-slate-400 shrink-0" />
      </button>

      {open && ReactDOM.createPortal(
        <div
          data-picker-popup={testId}
          style={popupStyle}
          className="bg-white border border-slate-300 shadow-2xl flex flex-col"
        >
          <div className="relative p-2 border-b border-slate-200 bg-slate-50 shrink-0">
            <MagnifyingGlass size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              data-testid={`${testId}-search`}
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, model, brand, SKU, HSN…"
              className="rounded-none w-full bg-white border border-slate-300 focus:border-[#002FA7] pl-7 pr-8 py-1.5 text-xs outline-none"
            />
            {query && (
              <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={11} /></button>
            )}
          </div>

          {/* Manual option — always first */}
          <button
            data-testid={`${testId}-manual`}
            type="button"
            onClick={handleManual}
            className={`text-left px-3 py-2 text-xs border-b border-slate-100 hover:bg-blue-50 flex items-center gap-2 shrink-0 ${!value ? "bg-blue-50/60" : ""}`}
          >
            <Tag size={12} weight="duotone" className="text-slate-500" />
            <span className="font-semibold">— Manual Item —</span>
            <span className="text-slate-400 ml-auto text-[10px]">Type anything in description</span>
          </button>

          {/* Product list */}
          <div className="overflow-y-auto flex-1 min-h-0">
            {filtered.length === 0 ? (
              <div data-testid={`${testId}-empty`} className="p-6 text-center text-xs text-slate-500">
                <div className="mb-2">No products found{query ? ` for "${query}"` : ""}.</div>
                <button
                  type="button"
                  onClick={handleManual}
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-[#002FA7] hover:underline"
                >
                  <Plus size={10} weight="bold" /> Add as manual item
                </button>
              </div>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.product_id}
                  data-testid={`${testId}-opt-${p.product_id}`}
                  type="button"
                  onClick={() => handleSelect(p)}
                  className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 hover:bg-blue-50 ${value === p.product_id ? "bg-blue-50/60" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="truncate">
                      <div className="font-medium text-slate-800 truncate">{p.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {[p.brand_name || p.brand, p.model, p.sku, p.category].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[11px] font-semibold tabular-nums">₹{Number(p.price || p.retail_price || 0).toLocaleString("en-IN")}</div>
                      {(p.hsn || p.hsn_sac) && <div className="text-[9px] font-mono text-slate-400">HSN {p.hsn || p.hsn_sac}</div>}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
          <div className="px-3 py-1.5 text-[10px] text-slate-400 border-t border-slate-200 bg-slate-50 shrink-0">
            {filtered.length} of {products.length} products
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ProductPicker;
